import React, { useState, useRef, useEffect } from 'react';
import { Phone, X, Clock, Users, ArrowDownLeft, ArrowUpRight, Copy, Check, Mic, MicOff, Plus, Video, UserCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { formatToE164 } from '../lib/twilio';
import { format } from 'date-fns';

// Define the TwilioCall interface
interface TwilioCall {
  id: string;
  business_id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  direction: 'inbound' | 'outbound';
  status: string;
  duration_seconds: number;
  call_start_time: string;
  created_at: string;
  // Add customer fields
  communication_id?: string;
  first_name?: string;
  last_name?: string;
  customer_phone?: string;
  customer_email?: string;
  is_supplier?: boolean;
}

// Enum for active tab
enum Tab {
  Keypad = 'keypad',
  Recents = 'recents',
  Contacts = 'contacts',
}

export default function Dialler() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recentCalls, setRecentCalls] = useState<TwilioCall[]>([]);
  const [isLoadingCalls, setIsLoadingCalls] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.Keypad);
  const [copiedNumber, setCopiedNumber] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { selectedBusiness, businesses } = useBusiness();
  
  // New call state variables
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callRecipient, setCallRecipient] = useState<string>('');
  const [showKeypadDuringCall, setShowKeypadDuringCall] = useState(false);

  const handleKeyPress = (key: string) => {
    setPhoneNumber(prev => prev + key);
    // Focus the input after pressing a key on the keypad
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  const handleClear = () => {
    setPhoneNumber('');
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCall = async () => {
    // Debug logging
    console.log("Debug - Phone Number:", phoneNumber);
    console.log("Debug - Selected Business:", selectedBusiness);
    
    if (!phoneNumber) {
      console.log("Debug - No phone number entered");
      toast.error('Please enter a phone number');
      return;
    }

    if (!selectedBusiness?.id) {
      console.log("Debug - No business selected:", selectedBusiness);
      toast.error('No business context available. Please select a business first.');
      return;
    }
    
    if (!supabase) {
      console.log("Debug - Supabase not available");
      toast.error('Database connection not available');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Format the phone number to E.164 format
      const formattedToNumber = formatToE164(phoneNumber);
      console.log("Debug - Formatted Phone Number:", formattedToNumber);
      
      // Use edge function to make the call without creating a database record first
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      console.log("Debug - Auth Session:", { 
        hasSession: sessionData?.session ? true : false,
        hasToken: accessToken ? true : false 
      });
      
      // Use the business ID from the current logged-in user's context
      // Instead of hardcoding a business ID that might not exist
      const businessId = selectedBusiness?.id;
      
      if (!businessId) {
        console.error("No business ID available from context");
        toast.error("No business selected. Please select a business first.");
        setIsLoading(false);
        return;
      }
      
      console.log("Using business ID from context:", businessId);
      
      // Make sure phone number starts with + and has no spaces
      const cleanToNumber = formattedToNumber.replace(/\s/g, '');
      
      // Get the business phone number to use as the 'from' number (it's a business call)
      let formattedFromNumber = '';
      if (selectedBusiness?.phone) {
        formattedFromNumber = formatToE164(selectedBusiness.phone);
      }
      
      // Build the request body
      const requestBody = {
        business_id: businessId,
        to: cleanToNumber
      };
      console.log("Debug - Request Body:", requestBody);
      
      try {
        const response = await fetch(
          'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1/twilio-make-call',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify(requestBody)
          }
        );
        
        const result = await response.json();
        console.log("Debug - Response from Edge Function:", result);
        
        if (!response.ok) {
          console.error('Error response from Edge Function:', result);
          toast.error(result.error || 'Failed to make call');
          setIsLoading(false);
          return;
        }
        
        // If successful, create call record with the call SID we received
        if (result.success && result.call_sid) {
          console.log("Debug - Received call_sid:", result.call_sid);
          
          try {
            const { error: callError } = await supabase
              .from('twilio_calls')
              .insert({
                business_id: businessId,
                from_number: result.from_number || formattedFromNumber,
                to_number: cleanToNumber,
                direction: 'outbound',
                status: 'ringing',
                call_sid: result.call_sid,
                call_start_time: new Date().toISOString()
              });
              
            if (callError) {
              console.error('Failed to create call record:', callError);
              // Show error toast but don't fail the overall operation
              toast.error('Call connected but failed to save to history');
            } else {
              console.log('Call record created successfully');
            }
          } catch (dbError) {
            console.error('Error creating call record:', dbError);
            toast.error('Call connected but failed to save to history');
          }
          
          // Set the call in progress state
          setIsCallInProgress(true);
          setCallStartTime(new Date());
          setCallRecipient(phoneNumber);
          
          toast.success('Call initiated');
          
          // Refresh the call history
          fetchRecentCalls();
        } else {
          toast.error('Call failed to connect');
        }
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        toast.error('Network error while making call');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to make call');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndCall = () => {
    // In a real app, you would call the Twilio API to end the call
    setIsCallInProgress(false);
    setIsMuted(false);
    setIsSpeakerOn(false);
    setShowKeypadDuringCall(false);
    toast.success('Call ended');
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    // In a real app, you would trigger the mute functionality via Twilio
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
    // In a real app, you would trigger the speaker functionality via Twilio
  };

  const toggleKeypadDuringCall = () => {
    setShowKeypadDuringCall(!showKeypadDuringCall);
  };

  // Fetch recent calls from the database
  const fetchRecentCalls = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setIsLoadingCalls(true);
      
      const { data, error } = await supabase
        .from('twilio_calls')
        .select('*, communication_id, first_name, last_name, customer_phone, customer_email')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) {
        console.error('Error fetching recent calls:', error);
        toast.error('Failed to load call history');
      } else {
        setRecentCalls(data || []);
      }
    } catch (error) {
      console.error('Error in fetchRecentCalls:', error);
    } finally {
      setIsLoadingCalls(false);
    }
  };

  // Call the number from recent calls
  const callFromRecent = (number: string) => {
    setPhoneNumber(number);
    setActiveTab(Tab.Keypad);
    // Focus the input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Format the phone number for display
  const formatPhoneNumber = (number: string) => {
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    
    // Format based on length (assuming US numbers, modify as needed)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else {
      return number; // Return original if can't format
    }
  };

  // Copy phone number to clipboard
  const copyToClipboard = (number: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent button click
    navigator.clipboard.writeText(number).then(() => {
      setCopiedNumber(number);
      toast.success('Phone number copied to clipboard');
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedNumber(null), 2000);
    }).catch((err) => {
      console.error('Failed to copy number:', err);
      toast.error('Failed to copy number');
    });
  };

  // Focus the input field when the component mounts and fetch recent calls
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Fetch recent calls on mount
    fetchRecentCalls();
    
    // Set up a real-time subscription to update call status
    const subscription = supabase
      ?.channel('twilio_calls_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'twilio_calls',
          filter: `business_id=eq.${selectedBusiness?.id}` 
        }, 
        (payload) => {
          console.log('Real-time update received:', payload);
          // Refresh the calls
          fetchRecentCalls();
        }
      )
      .subscribe();
    
    // Cleanup subscription when component unmounts
    return () => {
      subscription?.unsubscribe();
    };
  }, [selectedBusiness?.id]);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="p-4 flex flex-col justify-start items-center flex-grow">
        <h1 className="text-2xl font-semibold mb-4">Dialler</h1>
        
        {/* Call in progress view */}
        {isCallInProgress ? (
          <div className="flex flex-col items-center justify-between h-full w-full">
            <div className="flex flex-col items-center mt-10 mb-auto">
              <h2 className="text-2xl font-semibold mb-1">{callRecipient}</h2>
              <p className="text-gray-500">calling mobile...</p>
            </div>
            
            {showKeypadDuringCall ? (
              /* Keypad during call */
              <div className="grid grid-cols-3 gap-5 mb-8">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => (
                  <button
                    key={key}
                    className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center text-xl"
                    onClick={() => {
                      // In a real app, this would send DTMF tones via Twilio
                      console.log(`Pressed ${key} during call`);
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            ) : (
              /* Call controls */
              <div className="grid grid-cols-3 gap-x-12 gap-y-8 mb-8">
                <button 
                  className="flex flex-col items-center" 
                  onClick={toggleMute}
                >
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center ${isMuted ? 'bg-gray-400' : 'bg-gray-200'}`}>
                    {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                  </div>
                  <span className="text-xs mt-1">mute</span>
                </button>
                
                <button 
                  className="flex flex-col items-center" 
                  onClick={toggleKeypadDuringCall}
                >
                  <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center">
                    <div className="grid grid-cols-3 gap-0.5">
                      {[...Array(9)].map((_, i) => (
                        <div key={i} className="w-1 h-1 bg-gray-700 rounded-full" />
                      ))}
                    </div>
                  </div>
                  <span className="text-xs mt-1">keypad</span>
                </button>
                
                <button 
                  className="flex flex-col items-center" 
                  onClick={toggleSpeaker}
                >
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center ${isSpeakerOn ? 'bg-gray-400' : 'bg-gray-200'}`}>
                    <svg viewBox="0 0 24 24" className="h-6 w-6" stroke="currentColor" fill="none" strokeWidth="2">
                      <path d="M11 5L6 9H2v6h4l5 4V5z" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  </div>
                  <span className="text-xs mt-1">speaker</span>
                </button>
                
                <button className="flex flex-col items-center">
                  <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center">
                    <Plus className="h-6 w-6" />
                  </div>
                  <span className="text-xs mt-1">add call</span>
                </button>
                
                <button className="flex flex-col items-center">
                  <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center">
                    <Video className="h-6 w-6" />
                  </div>
                  <span className="text-xs mt-1">FaceTime</span>
                </button>
                
                <button className="flex flex-col items-center">
                  <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCircle className="h-6 w-6" />
                  </div>
                  <span className="text-xs mt-1">contacts</span>
                </button>
              </div>
            )}
            
            {/* Hang up button */}
            <button 
              className="h-16 w-16 rounded-full bg-red-500 flex items-center justify-center mb-10"
              onClick={handleEndCall}
            >
              <Phone className="h-8 w-8 text-white transform rotate-135" />
            </button>
          </div>
        ) : (
          <>
            {/* Regular dialer view - this is existing code */}
            {activeTab === Tab.Keypad && (
              <>
                <div className="w-full max-w-md mb-4">
                  <div className="flex items-center mb-4">
                    <input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full px-4 py-2 text-2xl text-center border rounded"
                      placeholder="Enter phone number"
                    />
                    {phoneNumber && (
                      <button
                        onClick={() => setPhoneNumber('')}
                        className="ml-2 p-2 bg-gray-200 rounded-full"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Numeric keypad */}
                    {[
                      { num: '1', letters: '' },
                      { num: '2', letters: 'ABC' },
                      { num: '3', letters: 'DEF' },
                      { num: '4', letters: 'GHI' },
                      { num: '5', letters: 'JKL' },
                      { num: '6', letters: 'MNO' },
                      { num: '7', letters: 'PQRS' },
                      { num: '8', letters: 'TUV' },
                      { num: '9', letters: 'WXYZ' },
                      { num: '*', letters: '' },
                      { num: '0', letters: '+' },
                      { num: '#', letters: '' }
                    ].map((key) => (
                      <button
                        key={key.num}
                        onClick={() => setPhoneNumber(prev => prev + key.num)}
                        className="p-4 bg-gray-100 rounded-full flex flex-col items-center justify-center h-16"
                      >
                        <span className="text-xl">{key.num}</span>
                        <span className="text-xs">{key.letters}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4 mt-4">
                  <button
                    onClick={handleCall}
                    disabled={isLoading || !phoneNumber}
                    className={`p-4 rounded-full ${
                      isLoading
                        ? 'bg-gray-300'
                        : phoneNumber
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-gray-300'
                    } text-white flex items-center justify-center w-14 h-14`}
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    ) : (
                      <Phone className="h-6 w-6" />
                    )}
                  </button>
                </div>
              </>
            )}
            
            {/* Recents view */}
            {activeTab === Tab.Recents && (
              <div className="w-full max-w-md overflow-auto">
                <h2 className="text-lg font-semibold mb-2">Recent Calls</h2>
                {isLoadingCalls ? (
                  <div className="flex justify-center my-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                ) : recentCalls.length === 0 ? (
                  <p className="text-center text-gray-500 my-8">No recent calls</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {recentCalls.map((call) => (
                      <li key={call.id} className="py-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center">
                              {call.direction === 'inbound' ? (
                                <ArrowDownLeft className="h-4 w-4 text-blue-500 mr-2" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-green-500 mr-2" />
                              )}
                              <p className="font-medium">
                                {/* Display customer name if available, otherwise phone number */}
                                {call.communication_id && call.first_name && call.last_name
                                  ? `${call.first_name} ${call.last_name}`
                                  : (call.direction === 'inbound' ? call.from_number : call.to_number)
                                }
                              </p>
                            </div>
                            <p className="text-xs text-gray-500">
                              {format(new Date(call.created_at), 'MMM d, yyyy h:mm a')}
                              {' · '}
                              {call.status}
                              {call.duration_seconds > 0 && ` · ${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, '0')}`}
                              {/* Show phone number as secondary info when displaying customer name */}
                              {call.communication_id && call.first_name && call.last_name && 
                                ` · ${call.direction === 'inbound' ? call.from_number : call.to_number}`
                              }
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button 
                              className="p-2 text-gray-500 hover:text-gray-700"
                              onClick={() => {
                                navigator.clipboard.writeText(call.direction === 'inbound' ? call.from_number : call.to_number);
                                setCopiedNumber(call.direction === 'inbound' ? call.from_number : call.to_number);
                                setTimeout(() => setCopiedNumber(null), 2000);
                              }}
                            >
                              {copiedNumber === (call.direction === 'inbound' ? call.from_number : call.to_number) ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <Copy className="h-5 w-5" />
                              )}
                            </button>
                            <button 
                              className="p-2 text-green-500 hover:text-green-700"
                              onClick={() => {
                                setPhoneNumber(call.direction === 'inbound' ? call.from_number : call.to_number);
                                setActiveTab(Tab.Keypad);
                              }}
                            >
                              <Phone className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Contacts tab */}
            {activeTab === Tab.Contacts && (
              <div className="w-full max-w-md">
                <h2 className="text-lg font-semibold mb-2">Contacts</h2>
                <p className="text-center text-gray-500 my-8">Contacts feature coming soon</p>
              </div>
            )}

            {/* Bottom tab navigation */}
            <div className="fixed bottom-0 left-0 right-0 flex justify-around items-center py-2 bg-white border-t border-gray-200">
              <button 
                onClick={() => setActiveTab(Tab.Recents)}
                className={`flex flex-col items-center px-3 py-1 ${activeTab === Tab.Recents ? 'text-blue-500' : 'text-gray-500'}`}
              >
                <div className="relative">
                  <Clock className="h-6 w-6" />
                  {recentCalls.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {recentCalls.length > 9 ? '9+' : recentCalls.length}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1">Recents</span>
              </button>
              <button 
                onClick={() => setActiveTab(Tab.Contacts)}
                className={`flex flex-col items-center px-3 py-1 ${activeTab === Tab.Contacts ? 'text-blue-500' : 'text-gray-500'}`}
              >
                <Users className="h-6 w-6" />
                <span className="text-xs mt-1">Contacts</span>
              </button>
              <button 
                onClick={() => setActiveTab(Tab.Keypad)}
                className={`flex flex-col items-center px-3 py-1 ${activeTab === Tab.Keypad ? 'text-blue-500' : 'text-gray-500'}`}
              >
                <Phone className="h-6 w-6" />
                <span className="text-xs mt-1">Keypad</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 